import { getApiBaseUrl } from '../../utils/environment.mjs';
import axios from "axios";

const API_URL = getApiBaseUrl();

class InvoiceService {
  
  // `onUploadProgress` (optional) receives axios's real byte progress, so the
  // uploader can show how far the files have got instead of a timer.
  async uploadInvoices(files, claimId = null, { onUploadProgress } = {}) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('invoices', file);
    });
    // When present, append this batch to the existing claim (reusable uploader).
    if (claimId) formData.append('claimId', claimId);

    // A plain axios `timeout` also counts the time spent SENDING the files, so
    // on a slow mobile uplink it aborted uploads that were still progressing.
    // Instead: abort if the upload makes no progress for a minute, or if the
    // server has not answered two minutes after the last byte was sent. Either
    // way a stalled request no longer leaves the uploader spinning forever.
    const STALL_MS = 60000;
    const SERVER_MS = 120000;
    const STALLED = 'The upload stopped making progress. Please check your connection and try again.';
    const SLOW_SERVER = 'The server took too long to respond. Please check the claim before retrying - the files may already have been added.';
    const controller = new AbortController();
    let timer = null;
    let abortReason = null;
    const arm = (ms, reason) => {
      clearTimeout(timer);
      timer = setTimeout(() => { abortReason = reason; controller.abort(); }, ms);
    };
    arm(STALL_MS, STALLED);

    try {
      const response = await axios.post(`${API_URL}/invoices/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        signal: controller.signal,
        onUploadProgress: (event) => {
          // Every progress event resets the stall watchdog; once all bytes are
          // sent, only the server's own time is limited.
          if (event && event.total && event.loaded >= event.total) {
            arm(SERVER_MS, SLOW_SERVER);
          } else {
            arm(STALL_MS, STALLED);
          }
          if (onUploadProgress) onUploadProgress(event);
        }
      });

      return response.data;
    } catch (error) {
      // Our own abort surfaces as axios's generic "canceled"; say why instead.
      if (abortReason && axios.isCancel(error)) throw new Error(abortReason);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Read one uploaded invoice and get back the fields found on it.
   *
   * The server does not write anything: the form merges the result into the
   * row's EMPTY fields only, and the user saves as usual. Resolves with
   * { status: 'ok'|'failed'|'disabled', invoiceId, fields, receiptNumberSource,
   * descriptionPrefill }. Network errors and timeouts reject.
   */
  async extractInvoice(invoiceId) {
    const response = await axios.post(
      `${API_URL}/invoices/${encodeURIComponent(invoiceId)}/extract`,
      {},
      {
        withCredentials: true,
        // The server caps each Document AI call at about 35s; this leaves room
        // for the download and the response without waiting on a hung request.
        timeout: 45000
      }
    );
    return response.data;
  }

  async uploadManualInvoice(file, itemId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('itemId', itemId);

    try {
      const response = await axios.post(`${API_URL}/invoices/manual-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // NEW METHOD: Upload manual invoice with claimId
  async uploadManualInvoiceWithClaimId(file, itemId, claimId) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('itemId', itemId);
      // claimId is optional - on a fresh form the backend lazily creates a draft.
      if (claimId) formData.append('claimId', claimId);

      const response = await axios.post(`${API_URL}/invoices/manual-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 30000 // 30 second timeout for file uploads
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          invoiceId: response.data.invoiceId,
          pdfUrl: response.data.pdfUrl,
          itemId: response.data.itemId,
          claimId: response.data.claimId,
          tempId: response.data.tempId,
          extracted: response.data.extracted,
          // Whether this claim's owner is in a region where invoices are read
          // automatically. The form only asks for extraction when it is true.
          extractionEnabled: response.data.extractionEnabled === true,
          // Background virus-scan state of the new file (normally 'pending').
          scanStatus: response.data.scanStatus,
          message: response.data.message
        };
      } else {
        throw new Error(response.data?.message || 'Upload failed - invalid response');
      }
    } catch (error) {
      
      // Enhanced error handling
      if (error.response) {
        // Server responded with error
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           `Server error: ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        // Network error
        throw new Error('Network error - please check your connection and try again');
      } else {
        // Other error
        throw new Error(error.message || 'Upload failed');
      }
    }
  }

  // Create draft claim for manual entry
  async createDraftClaim() {
    try {
      
      const response = await axios.post(`${API_URL}/expenses/create-draft`, {}, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          claimId: response.data.claimId,
          isNewClaim: response.data.isNewClaim,
          itemCount: response.data.itemCount,
          message: response.data.message
        };
      } else {
        throw new Error(response.data?.error || 'Failed to create draft claim');
      }
    } catch (error) {
      
      if (error.response?.status === 404) {
        throw new Error('Draft claim endpoint not found. Please contact support.');
      }
      
      if (error.response) {
        const errorMessage = error.response.data?.error || 
                          error.response.data?.message || 
                          `Server error: ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error('Network error - please check your connection');
      } else {
        throw new Error(error.message || 'Failed to create draft claim');
      }
    }
  }


  async getExpenseClaim(claimId) {
    try {
      const response = await axios.get(`${API_URL}/expenses/claim/${claimId}`, {
        withCredentials: true
      });
      
      return response.data.claim;
    } catch (error) {
      throw error;
    }
  }

  async deleteEntireClaim(claimId) {
    try {
      const response = await axios.delete(`${API_URL}/perdiem/claims/${claimId}`, {
        withCredentials: true
      });
   
      return response.data.data;
    } catch (error) {
      throw error;
    }
  }

  // Additional method for expense claims deletion if needed
  async deleteExpenseClaim(claimId) {
    try {
      const response = await axios.delete(`${API_URL}/expenses/claim/${claimId}`, {
        withCredentials: true
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default InvoiceService;

